import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const defaultLinkForm = {
  amount: '',
  currency: 'PLN',
  description: '',
  customer_name: '',
  customer_email: '',
  site_binding_id: '',
  source_domain: '',
};

const defaultBindingForm = {
  domain: '',
  site_name: '',
  genro_site_id: '',
  genro_site_subdomain: '',
  genro_site_status: 'published',
};

const cardStyle = {
  background: 'rgba(255,255,255,0.05)',
  borderRadius: 12,
  padding: 16,
};

const inputStyle = {
  width: '100%',
  padding: 12,
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.05)',
  color: '#fff',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: 6,
  opacity: 0.75,
  fontSize: 13,
};

export default function PaymentLinks() {
  const { user } = useApp();
  const { t } = useI18n();
  const [links, setLinks] = useState([]);
  const [siteBindings, setSiteBindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingBinding, setSavingBinding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBindingForm, setShowBindingForm] = useState(false);
  const [editingBindingId, setEditingBindingId] = useState(null);
  const [form, setForm] = useState(defaultLinkForm);
  const [bindingForm, setBindingForm] = useState(defaultBindingForm);
  const [message, setMessage] = useState(null);
  const [copied, setCopied] = useState(null);

  const MIN_AMOUNT = { PLN: 2, EUR: 1, USD: 1 };

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [linksRes, bindingsRes] = await Promise.all([
        api.get('/payment-links/my').catch(() => ({ data: [] })),
        api.get('/site-bindings/my').catch(() => ({ data: [] })),
      ]);
      setLinks(linksRes.data || []);
      setSiteBindings(bindingsRes.data || []);
    } finally {
      setLoading(false);
    }
  }

  function resetLinkForm(next = {}) {
    setForm({ ...defaultLinkForm, ...next });
  }

  function resetBindingForm(next = {}) {
    setBindingForm({ ...defaultBindingForm, ...next });
  }

  function onBindingSelect(value) {
    const selected = siteBindings.find((item) => String(item.id) === String(value));
    setForm((prev) => ({
      ...prev,
      site_binding_id: value,
      source_domain: selected?.domain || '',
    }));
  }

  function startCreateLink(binding = null) {
    if (binding) {
      resetLinkForm({
        site_binding_id: String(binding.id),
        source_domain: binding.domain || '',
        description: binding.site_name ? `Оплата за ${binding.site_name}` : '',
      });
    }
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function startCreateBinding() {
    setEditingBindingId(null);
    resetBindingForm();
    setShowBindingForm(true);
  }

  function startEditBinding(binding) {
    setEditingBindingId(binding.id);
    resetBindingForm({
      domain: binding.domain || '',
      site_name: binding.site_name || '',
      genro_site_id: binding.genro_site_id || '',
      genro_site_subdomain: binding.genro_site_subdomain || '',
      genro_site_status: binding.genro_site_status || 'published',
    });
    setShowBindingForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function saveBinding() {
    if (!bindingForm.domain.trim()) {
      setMessage({ type: 'error', text: 'Укажи домен сайта' });
      return;
    }

    setSavingBinding(true);
    try {
      const payload = {
        domain: bindingForm.domain.trim(),
        site_name: bindingForm.site_name.trim() || null,
        genro_site_id: bindingForm.genro_site_id ? Number(bindingForm.genro_site_id) : null,
        genro_site_subdomain: bindingForm.genro_site_subdomain.trim() || null,
        genro_site_status: bindingForm.genro_site_status || null,
      };

      if (editingBindingId) {
        await api.put(`/site-bindings/${editingBindingId}`, payload);
        setMessage({ type: 'success', text: '✅ Site binding обновлён' });
      } else {
        await api.post('/site-bindings/create', payload);
        setMessage({ type: 'success', text: '✅ Site binding создан' });
      }

      setShowBindingForm(false);
      setEditingBindingId(null);
      resetBindingForm();
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка сохранения site binding' });
    } finally {
      setSavingBinding(false);
    }
  }

  async function deleteBinding(binding) {
    const ok = window.confirm(`Убрать привязку сайта ${binding.domain}?`);
    if (!ok) return;
    try {
      await api.delete(`/site-bindings/${binding.id}`);
      setMessage({ type: 'success', text: '✅ Site binding архивирован' });
      if (String(form.site_binding_id) === String(binding.id)) {
        resetLinkForm();
      }
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка удаления site binding' });
    }
  }

  async function createLink() {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setMessage({ type: 'error', text: 'Укажите сумму' });
      return;
    }
    const minAmount = MIN_AMOUNT[form.currency] || 1;
    if (parseFloat(form.amount) < minAmount) {
      setMessage({ type: 'error', text: `Минимум для ${form.currency}: ${minAmount}` });
      return;
    }
    if (!form.description.trim()) {
      setMessage({ type: 'error', text: 'Укажите описание товара/услуги' });
      return;
    }

    setCreating(true);
    try {
      await api.post('/payment-links/create', {
        amount: parseFloat(form.amount),
        currency: form.currency,
        description: form.description.trim(),
        customer_name: form.customer_name.trim() || null,
        customer_email: form.customer_email.trim() || null,
        site_binding_id: form.site_binding_id ? Number(form.site_binding_id) : null,
        source_domain: form.source_domain || null,
      });
      setMessage({ type: 'success', text: '✅ Ссылка на оплату создана!' });
      resetLinkForm();
      setShowForm(false);
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка создания' });
    } finally {
      setCreating(false);
    }
  }

  async function deleteLink(link) {
    const ok = window.confirm('Удалить эту ссылку?');
    if (!ok) return;
    try {
      await api.delete(`/payment-links/${link.id}`);
      setMessage({ type: 'success', text: '✅ Ссылка удалена' });
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка удаления' });
    }
  }

  async function copyLink(link) {
    if (!link.payment_url) {
      setMessage({ type: 'error', text: 'Stripe-ссылка не создана. Повторите попытку.' });
      return;
    }
    await navigator.clipboard.writeText(link.payment_url);
    setCopied(link.id);
    setTimeout(() => setCopied(null), 2000);
  }

  function shareLink(link) {
    if (!link.payment_url) {
      setMessage({ type: 'error', text: 'Stripe-ссылка не создана. Повторите попытку.' });
      return;
    }
    const url = link.payment_url;
    if (navigator.share) {
      navigator.share({
        title: `Оплата: ${link.description}`,
        text: `Оплатите ${link.amount} ${link.currency} за: ${link.description}`,
        url,
      }).catch(() => {});
    } else {
      copyLink(link);
    }
  }

  if (loading) {
    return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;
  }

  return (
    <div className="page">
      <BackButton />
      <h1>💳 Ссылки на оплату</h1>

      {message && (
        <div style={{
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          background: message.type === 'error' ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)',
        }}>
          {message.text}
        </div>
      )}

      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🌐 Сайт → оплата → invoice</div>
        <p style={{ fontSize: 13, opacity: 0.95, margin: 0 }}>
          Привяжи свой сайт или домен один раз, и дальше создавай payment links уже от конкретного сайта.
          Тогда источник сайта сохранится в платеже и в invoice хвосте Workify.
        </p>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0 }}>🔗 Мои сайты / домены</h3>
            <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>
              {siteBindings.length} привязок • user: {user?.telegram_username || user?.first_name || 'client'}
            </div>
          </div>
          {!showBindingForm && (
            <button className="btn btn-primary" onClick={startCreateBinding}>➕ Добавить сайт</button>
          )}
        </div>

        {showBindingForm && (
          <div style={{ ...cardStyle, background: 'rgba(255,255,255,0.03)', marginBottom: 12 }}>
            <h4 style={{ marginTop: 0 }}>{editingBindingId ? 'Редактировать site binding' : 'Новый site binding'}</h4>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Домен *</label>
              <input
                type="text"
                value={bindingForm.domain}
                onChange={(e) => setBindingForm({ ...bindingForm, domain: e.target.value })}
                placeholder="rabatprodukt.pl"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Название сайта</label>
              <input
                type="text"
                value={bindingForm.site_name}
                onChange={(e) => setBindingForm({ ...bindingForm, site_name: e.target.value })}
                placeholder="Discount MAX Kato"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Genro site ID</label>
                <input
                  type="number"
                  value={bindingForm.genro_site_id}
                  onChange={(e) => setBindingForm({ ...bindingForm, genro_site_id: e.target.value })}
                  placeholder="14"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Genro subdomain</label>
                <input
                  type="text"
                  value={bindingForm.genro_site_subdomain}
                  onChange={(e) => setBindingForm({ ...bindingForm, genro_site_subdomain: e.target.value })}
                  placeholder="rabatprodukt"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Статус Genro</label>
              <select
                value={bindingForm.genro_site_status}
                onChange={(e) => setBindingForm({ ...bindingForm, genro_site_status: e.target.value })}
                style={inputStyle}
              >
                <option value="published">published</option>
                <option value="generated">generated</option>
                <option value="draft">draft</option>
                <option value="paused">paused</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={saveBinding} disabled={savingBinding}>
                {savingBinding ? '⏳...' : editingBindingId ? '💾 Сохранить' : '✅ Создать'}
              </button>
              <button
                className="btn"
                style={{ background: 'rgba(255,255,255,0.1)' }}
                onClick={() => {
                  setShowBindingForm(false);
                  setEditingBindingId(null);
                  resetBindingForm();
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        )}

        {siteBindings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, opacity: 0.75 }}>
            Пока нет ни одного привязанного сайта.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {siteBindings.map((binding) => (
              <div key={binding.id} style={{ ...cardStyle, background: 'rgba(255,255,255,0.03)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>🌐 {binding.domain}</div>
                    <div style={{ fontSize: 13, opacity: 0.75 }}>{binding.site_name || 'Без названия'}</div>
                  </div>
                  <div style={{
                    padding: '4px 8px',
                    borderRadius: 999,
                    fontSize: 11,
                    background: binding.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(107,114,128,0.2)',
                  }}>
                    {binding.status}
                  </div>
                </div>

                <div style={{ fontSize: 13, opacity: 0.8, display: 'grid', gap: 4, marginBottom: 12 }}>
                  <div>Company ID: {binding.company_id || '—'}</div>
                  <div>Genro site ID: {binding.genro_site_id || '—'}</div>
                  <div>Genro subdomain: {binding.genro_site_subdomain || '—'}</div>
                  <div>Genro status: {binding.genro_site_status || '—'}</div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={() => startCreateLink(binding)}>💳 Создать payment link</button>
                  <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => startEditBinding(binding)}>✏️ Изменить</button>
                  <button className="btn" style={{ background: 'rgba(220,38,38,0.16)' }} onClick={() => deleteBinding(binding)}>🗑 Убрать</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: 16 }}
          onClick={() => startCreateLink()}
        >
          ➕ Создать ссылку на оплату
        </button>
      )}

      {showForm && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Новая ссылка на оплату</h3>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Сайт / домен</label>
            <select
              value={form.site_binding_id}
              onChange={(e) => onBindingSelect(e.target.value)}
              style={inputStyle}
            >
              <option value="">Без привязки к сайту</option>
              {siteBindings.map((binding) => (
                <option key={binding.id} value={binding.id}>
                  {binding.domain}{binding.site_name ? ` — ${binding.site_name}` : ''}
                </option>
              ))}
            </select>
            {form.source_domain && (
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                Источник оплаты: {form.source_domain}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Сумма *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                style={{ ...inputStyle, flex: 1 }}
              />
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                style={{ ...inputStyle, width: 110 }}
              >
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Описание товара/услуги *</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Например: Консультация 1 час"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Имя клиента (необязательно)</label>
            <input
              type="text"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Иван Иванов"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email клиента (необязательно)</label>
            <input
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              placeholder="client@email.com"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={createLink} disabled={creating}>
              {creating ? '⏳...' : '✅ Создать'}
            </button>
            <button
              className="btn"
              style={{ background: 'rgba(255,255,255,0.1)' }}
              onClick={() => {
                setShowForm(false);
                resetLinkForm();
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      <h3>📋 Мои ссылки</h3>

      {links.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💳</div>
          <p style={{ opacity: 0.7 }}>Пока нет ссылок на оплату</p>
        </div>
      ) : (
        <div className="list">
          {links.map((link) => (
            <div key={link.id} style={{ ...cardStyle, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12, marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: 18 }}>{link.amount} {link.currency}</div>
                  <div style={{ opacity: 0.8 }}>{link.description}</div>
                  {link.source_domain && (
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>🌐 {link.source_domain}</div>
                  )}
                </div>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  fontSize: 11,
                  background: link.status === 'paid'
                    ? '#10b981'
                    : link.status === 'pending'
                      ? '#f59e0b'
                      : '#6b7280',
                }}>
                  {link.status === 'paid' ? '✅ Оплачено' : link.status === 'pending' ? '⏳ Ожидает' : '📝 Создано'}
                </span>
              </div>

              {link.customer_name && (
                <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
                  👤 {link.customer_name} {link.customer_email && `(${link.customer_email})`}
                </div>
              )}

              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.6 }}>ID: {link.id}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={() => copyLink(link)}>
                    {copied === link.id ? '✅ Скопировано' : '📋 Копировать'}
                  </button>
                  <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => shareLink(link)}>
                    📤 Поделиться
                  </button>
                  {link.status !== 'paid' && (
                    <button className="btn" style={{ background: 'rgba(220,38,38,0.16)' }} onClick={() => deleteLink(link)}>
                      🗑 Удалить
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
